import React, { useEffect, useRef, useState } from "react";
import {
  ActionButton,
  Badge,
  Heading,
  StatusLight,
  Text,
  View
} from "@adobe/react-spectrum";
import Emitter from "../../../emitter";
import { CASE_STATES } from "../../../constants/constants";
import { AlertCircleIcon, CloseIconBlack } from "../../../components/common/Icons";
import { IndividualTask } from "../../../amazon-connect/models/task";
import Polling from "../../../utils/polling";
import { API_CASE_UPDATE } from "../../../constants/events";


interface CaseInformationHeaderProps {
  activeCase: IndividualTask;
  tabName?: string;
}

const CaseInformationHeader: React.FC<CaseInformationHeaderProps> = ({
  activeCase,
  tabName,
}) => {
  const [caseDuration, setCaseDuration] = useState<any>(null);
  const pollingCase: any = useRef(null);
  let caseStartTime = activeCase?.self?.connectedTime;
  let caseEndTime = activeCase?.self?.endTime;
  let caseTime =
    caseEndTime && caseStartTime ? (caseEndTime - caseStartTime) / 1000 : 0;
  let priorityCode = activeCase?.customer?.attributes?.PRIORITYCODE;
  let priorityText = activeCase?.customer?.attributes?.PRIORITYTEXT;

  const formattedHistoryTime = () => {
    return new Date(caseTime * 1000).toISOString().slice(11, 19);
  };

  const getConnectedDuration = (caseDuration: number) => {
    let secs = Math.floor(caseDuration / 1000);
    let mins = 0;
    let hrs = 0;
    if (secs >= 60) {
      mins = Math.floor(secs / 60);
      secs = secs % 60;
    }
    if (mins >= 60) {
      hrs = Math.floor(mins / 60);
      mins = mins % 60;
    }

    const padNumber = (num: number) => num?.toString()?.padStart(2, "0");

    return `${padNumber(hrs)}:${padNumber(mins)}:${padNumber(secs)}`;
  }



  useEffect(() => {
    // Stop any existing timer
    if (pollingCase.current) {
      pollingCase.current.stop();
    }

    // Reset the duration state
    setCaseDuration(null);

    // Start a new timer if the new active case is established
    if (activeCase?.self?.state === CASE_STATES.ESTABLISHED) {
      pollingCase.current = new Polling(timer, 1000);
      startCaseTimer();
    }

    // Cleanup on unmount or when activeCase changes
    return () => {
      if (pollingCase.current) {
        pollingCase.current.stop();
      }
    };
  }, [activeCase?.id]);


  useEffect(() => {
    if (activeCase?.self?.state === CASE_STATES.ESTABLISHED) {
      if (!pollingCase.current || !pollingCase.current.hasStarted()) {
        pollingCase.current = new Polling(timer, 1000);
        startCaseTimer();
      }
    }

    return () => {
      if (pollingCase.current) {
        pollingCase.current.stop();
      }
    };
  }, [activeCase?.self?.state]);

  const startCaseTimer = () => {
    if (!pollingCase.current.hasStarted()) {
      pollingCase.current.start();
    }
  };

  const timer = (continuePoll: any, pollingInstance: any) => {
    if (!activeCase || !activeCase.id) {
      pollingInstance.stop();
      setCaseDuration(null);
      return;
    }

    const connectedTime = activeCase.self?.connectedTime || activeCase.customer?.connectedTime;
    const computedCaseDuration = connectedTime
      ? new Date().getTime() -
        new Date(connectedTime).getTime() > 0
        ? new Date().getTime() -
        new Date(connectedTime).getTime()
        : 0
      : 0;
    setCaseDuration(computedCaseDuration);
    continuePoll();
  };

  const closeCaseHandler = () => {
    Emitter.emit(API_CASE_UPDATE, {
      body: {
        participantId: activeCase.self?.id,
        conversationId: activeCase.id,
        state: "disconnected",
      },
    });
  };

  return (
    <View UNSAFE_className="caseInformationHeader">
      <View UNSAFE_className="caseInformationHeader-left">
        <Heading UNSAFE_className="case-title-ellipsis">
          {activeCase?.customer?.attributes.CASE_TITLE} ({activeCase?.customer?.attributes?.CASEID})
        </Heading>
        {(priorityCode && Number(priorityCode) === 1) && <Badge
          UNSAFE_className="caseInformationHeader-badge-compact"
          variant='negative'>
          <AlertCircleIcon color={'#fff'} fill='#d31510' title=""/>
          <Text UNSAFE_className="warningIcon-text">{priorityText}</Text>
        </Badge>}
      </View>
      {!tabName && (
        <View UNSAFE_className="caseInformationHeader-right">
          <View UNSAFE_className="caseInformationHeader-right-status">
            <Text UNSAFE_className="caseInformationHeader-right-status-text">Ongoing Case</Text>
            <StatusLight role="status" variant="positive" UNSAFE_style={{ margin: 0, paddingTop: 6 }} />
            <Text UNSAFE_className="caseInformationHeader-right-status-text">{getConnectedDuration(caseDuration)}</Text>
          </View>
          <ActionButton
            type="button"
            onPress={closeCaseHandler}
            UNSAFE_className="caseInformationHeader-button"
            isDisabled={!navigator.onLine}
          >
            <CloseIconBlack title="End Case" />
            <p className="caseInformationHeader-button_text">End Case</p>
          </ActionButton>
        </View>
      )}
      {tabName === "history" && (
        <View UNSAFE_className="caseInformationHeader-right-history">
          <Text UNSAFE_className="case-duration">Case Duration : </Text>
          <Text UNSAFE_className="case-duration-time">
            {formattedHistoryTime()}
          </Text>
        </View>
      )}
    </View>
  );
};

export default CaseInformationHeader;
